/**
 * Ocean's DENİZANASI — the jellyfish — in the shared 120×120 LINE asset box.
 *
 * One object across three assets, the shape this family likes best (Sakura's
 * blossom, Tallinn's flake, Rio's parasol): the Home tab, all four passcode dots
 * and the chat wallpaper draw this drawing.
 *
 * WHY A JELLYFISH AND NOT SOMETHING FROM THE ICON
 *
 * The theme's icon is a seabed — coral heads, seagrass, rising bubbles under
 * three bioluminescent currents — and the honest first instinct is to trace the
 * coral, the way Tallinn's towers were traced from its own icon. It was drawn
 * and dropped: a coral head has no silhouette. At the 22px a classic tab glyph
 * actually renders it is a lumpy blob, and at 30px on the lock screen it is the
 * same blob four times. What the icon is ABOUT — a dark sea lit from inside — is
 * carried better by the one animal that IS the bioluminescence the theme's own
 * description names. The coral keeps the menu strip, where it has 1472px to be
 * a coral garden in.
 *
 * WHAT IS LOAD-BEARING
 *
 *  - A SCALLOPED HEM, four lobes. It is the whole silhouette. A smooth dome over
 *    strands is a mushroom or a hot-air balloon; the scallops are what say
 *    "medusa" before anything else in the drawing is legible.
 *  - THE BELL IS WIDER THAN TALL, 82 across to 43 down. Drawn taller it reads as
 *    a squid's mantle, and a squid is a different animal with a different
 *    register.
 *  - FOUR TENTACLES AND TWO ORAL ARMS, never more. Real medusae trail dozens;
 *    dozens at this size is a smudge under a dome. The two arms are drawn WIDER
 *    than the four tentacles so the bundle has a front and a back rather than
 *    reading as a comb.
 *  - THE STRANDS ARE 3.4 UNITS THICK AND THEY TAPER TO 1.5, NOT TO NOTHING. LINE
 *    renders a passcode dot at roughly 30px, so one unit here is a quarter of a
 *    pixel: a strand that tapers honestly to a point loses its last third to the
 *    rasteriser and the animal ends up with stumps.
 *  - THE STRANDS ALL SWING THE SAME WAY at their tips. Alternating them reads as
 *    a spider; a common drift reads as water moving.
 *
 * Every subpath is wound clockwise so a nonzero fill unions the overlaps instead
 * of punching holes in them ([[line-shared-glyph-modules]]) — enforced by `cw()`
 * rather than by eye, because a mirrored strand's winding flips with it.
 *
 * The fit: the animal spans x 19..101 and y 11..112 of the 120 box, so it is
 * TALL — see the sizePct correction in line-menu-button, which is measured
 * against a rendered neighbour and not derived from this box.
 */

const f = ( v: number ) => +v.toFixed( 1 );

/** A polygon in clockwise order (y down), whichever order it arrives in. */
function cw( pts: [ number, number ][] ): string {
  let area = 0;
  for ( let i = 0; i < pts.length; i++ ) {
    const a = pts[ i ], b = pts[ ( i + 1 ) % pts.length ];
    area += a[ 0 ] * b[ 1 ] - b[ 0 ] * a[ 1 ];
  }
  const p = area < 0 ? pts.slice().reverse() : pts;
  return 'M' + p.map( ( q ) => `${ f( q[ 0 ] ) },${ f( q[ 1 ] ) }` ).join( ' L' ) + ' Z';
}

/* ---------------------------------------------------------------- geometry -- */

const CX = 60;
/** The bell: half-width, the crown, and where the hem's cusps sit. */
const BELL_RX = 41, BELL_TOP = 11, HEM = 54;
/** How deep each scallop of the hem dips, and how many there are. */
const LOBES = 4, LOBE_DROP = 11;
/** How far the hem's own line sags from its cusps at the edges to its middle —
 *  a bell is a dome seen slightly from below, so its rim is an ellipse and not a
 *  ruled line. Two units of it is enough to stop the hem reading as a shelf. */
const HEM_SAG = 2.5;
/** The deepest point of the hem: where the strands have to start from. */
const HEM_FLOOR = HEM + HEM_SAG + LOBE_DROP;

/** The hem's cusp height at x — the shallow ellipse the scallops hang off. */
function hemAt( x: number ): number {
  const t = ( x - CX ) / BELL_RX;
  return HEM + HEM_SAG * ( 1 - t * t );
}

/**
 * The bell: a dome from hem-left up over the crown and down to hem-right, then
 * the scalloped hem back again.
 *
 * The dome is two cubics rather than an arc, and their control points are the
 * part worth stating: the first pass put the side control at the CROWN's height,
 * which makes the sides run vertically out of the hem and turns the animal into
 * a bell jar. They sit at 0.55 of the dome's own height instead — the cubic
 * approximation of a quarter-circle — so the shoulders bulge and the widest part
 * of the bell is above the hem, where a medusa's is.
 */
export const JELLY_BELL = ( () => {
  const L = CX - BELL_RX, R = CX + BELL_RX;
  const k = ( HEM - BELL_TOP ) * 0.55;
  const dome =
    `M${ f( L ) },${ f( hemAt( L ) ) }`
    + ` C${ f( L ) },${ f( HEM - k ) } ${ f( CX - BELL_RX * 0.52 ) },${ BELL_TOP } ${ CX },${ BELL_TOP }`
    + ` C${ f( CX + BELL_RX * 0.52 ) },${ BELL_TOP } ${ f( R ) },${ f( HEM - k ) } ${ f( R ) },${ f( hemAt( R ) ) }`;
  // The hem, right to left, one scallop per lobe. Each dips LOBE_DROP below its
  // own cusp line and comes back up to the next cusp — DEEP, because this is the
  // whole silhouette: at LOBE_DROP 7 the dips vanished by 22px and the animal was
  // a plain dome over strings.
  const step = ( R - L ) / LOBES;
  let hem = '';
  for ( let i = 0; i < LOBES; i++ ) {
    const x0 = R - i * step, x1 = R - ( i + 1 ) * step;
    const mid = ( x0 + x1 ) / 2;
    hem += ` C${ f( x0 - step * 0.18 ) },${ f( hemAt( mid ) + LOBE_DROP ) }`
      + ` ${ f( x1 + step * 0.18 ) },${ f( hemAt( mid ) + LOBE_DROP ) } ${ f( x1 ) },${ f( hemAt( x1 ) ) }`;
  }
  return `${ dome }${ hem } Z`;
} )();

/**
 * The crown highlight: a crescent inside the top of the bell, so the ON state
 * has a lit side rather than being one flat dome.
 *
 * Held well inside the silhouette (never touching it) — a highlight that reaches
 * the edge stops being light on a curved body and becomes a second outline.
 */
export const JELLY_CREST =
  `M${ f( CX - 26 ) },${ f( BELL_TOP + 17 ) }`
  + ` C${ f( CX - 21 ) },${ f( BELL_TOP + 6 ) } ${ f( CX + 3 ) },${ f( BELL_TOP + 4 ) } ${ f( CX + 15 ) },${ f( BELL_TOP + 10 ) }`
  + ` C${ f( CX + 1 ) },${ f( BELL_TOP + 9 ) } ${ f( CX - 15 ) },${ f( BELL_TOP + 13 ) } ${ f( CX - 26 ) },${ f( BELL_TOP + 17 ) } Z`;

/**
 * One trailing strand: a ribbon that leaves the hem at `x`, waves `swing` units
 * to each side on its way down to `y1`, and tapers from `w0` to `w1`.
 *
 * Built as a polygon walked down one side and back up the other, so the taper is
 * real geometry rather than a stroke-width the renderer has to honour — a
 * stroked strand disappears the moment LINE scales the asset down.
 */
function strand( x: number, y0: number, y1: number, w0: number, w1: number,
                 swing: number, phase: number ): string {
  const STEPS = 16;
  const left: [ number, number ][] = [];
  const right: [ number, number ][] = [];
  for ( let i = 0; i <= STEPS; i++ ) {
    const t = i / STEPS;
    const y = y0 + ( y1 - y0 ) * t;
    // One and a half waves down the strand, with a common DRIFT added on top:
    // every strand ends up leaning the same way at its tip, which is what reads
    // as a current rather than as legs. Two full waves was the first pass and it
    // put a kink in every strand at the same height, which read as a tangle.
    const wave = Math.sin( t * Math.PI * 1.5 + phase ) * swing * t;
    const drift = t * t * 6;
    const cx = x + wave + drift;
    // The last eighth of the strand rounds off to a point instead of stopping on
    // a flat end. Cut square, a tapered ribbon reads as CUT RIBBON at 120 — the
    // eye finds the straight edge and the strand stops being organic — and at
    // 22px the flat end is the one part of it a pixel is wide enough to show.
    const round = t > 0.86 ? Math.sqrt( Math.max( 0, 1 - Math.pow( ( t - 0.86 ) / 0.14, 2 ) ) ) : 1;
    const w = ( w0 + ( w1 - w0 ) * t ) / 2 * round;
    left.push( [ cx - w, y ] );
    right.push( [ cx + w, y ] );
  }
  return cw( left.concat( right.reverse() ) );
}

/**
 * The four thin tentacles.
 *
 * They hang from UNDER the bell, not off its rim — spread across the middle 62%
 * of it. The first pass hung them on the scallop points, so the outermost pair
 * fell outside the animal's own shoulders and the bundle read as a comb rather
 * than as something trailing.
 *
 * They also start at HEM_FLOOR, the trough of the deepest scallop, so no strand
 * ever shows through the gap between two lobes. That gap was visible in the
 * first pass as a flat notch across the hem, which looked like a rendering fault
 * rather than a drawing.
 */
export const JELLY_TENTACLES = ( () => {
  const out: string[] = [];
  const xs = [ -0.74, -0.34, 0.34, 0.74 ];
  const ends = [ 100, 112, 108, 96 ];
  for ( let i = 0; i < xs.length; i++ ) {
    out.push( strand( CX + xs[ i ] * BELL_RX, HEM_FLOOR - 3, ends[ i ], 3.8, 1.5,
                      5.5, 0.5 + i * 1.15 ) );
  }
  return out.join( ' ' );
} )();

/**
 * The two oral arms: WIDER, shorter, hung from the middle of the bell.
 *
 * The width difference is what gives the bundle a front and a back. In the first
 * pass the arms were 8.5 units against the tentacles' 4.2 and the two families
 * still read as one — at 22px a two-to-one difference in a 3px feature is half a
 * pixel. At 9.5 against 3.8 they separate.
 *
 * They were briefly 12 units wide, which overshot in the other direction: two
 * arms that fat merge with each other under the bell and the four tentacles
 * disappear behind them, so the animal ends up with a trunk. The tentacles were
 * pushed out to 0.74 of the bell's half-width at the same time, which is what
 * gets them clear of the arms without hanging them off the rim.
 */
export const JELLY_ARMS = ( () => [
  strand( CX - 9, HEM_FLOOR - 6, 92, 9.5, 3, 4, 0.2 ),
  strand( CX + 9, HEM_FLOOR - 6, 98, 9.5, 3, 4, 2.4 ),
].join( ' ' ) )();

/**
 * THE CAPILLARIES (owner 2026-08-15: "içinde elektrik mavisi pembesi renkleri
 * olsun, kılcal damar gibi") — the fine vessel network inside the bell, drawn
 * only on the LIT Home tab.
 *
 * Two families in two hues, and they are two families rather than one drawing in
 * two colours on purpose: a real medusa's bell carries radial canals running
 * crown-to-rim and a ring canal threading across them, and giving each its own
 * colour is what stops the pair reading as one scribble at a small size.
 *
 * - JELLY_VEINS_MAIN, ELECTRIC BLUE: four radial canals leaving the crown and
 *   fanning to just short of the hem, each with one short offshoot. They stop
 *   short deliberately — a vessel that touches the silhouette becomes a notch in
 *   it the moment the rasteriser rounds a pixel.
 * - JELLY_VEINS_FINE, PINK: shorter, thinner cross-vessels hung between the blue
 *   ones, sitting lower in the bell where a ring canal actually runs.
 *
 * They are RIBBONS, not strokes, for the reason every other line in this module
 * is: a 1-unit stroke is a fifth of a pixel once LINE scales a 128px tab icon
 * down, and it disappears. At 1.6 units tapering to 0.7 they survive as a hint
 * of texture at 22px and read as vessels at 128.
 */
function vein( pts: [ number, number ][], w0: number, w1: number ): string {
  // Resampled through a Catmull-Rom so a four-point vessel is a curve rather than
  // a polyline with visible corners, then walked out to a tapered ribbon.
  const N = 26;
  const left: [ number, number ][] = [];
  const right: [ number, number ][] = [];
  const at = ( t: number ): [ number, number ] => {
    const seg = Math.min( pts.length - 2, Math.floor( t * ( pts.length - 1 ) ) );
    const u = t * ( pts.length - 1 ) - seg;
    const p0 = pts[ Math.max( 0, seg - 1 ) ], p1 = pts[ seg ];
    const p2 = pts[ seg + 1 ], p3 = pts[ Math.min( pts.length - 1, seg + 2 ) ];
    const h = ( a: number, b: number, c: number, d: number ) =>
      0.5 * ( ( 2 * b ) + ( -a + c ) * u + ( 2 * a - 5 * b + 4 * c - d ) * u * u
        + ( -a + 3 * b - 3 * c + d ) * u * u * u );
    return [ h( p0[ 0 ], p1[ 0 ], p2[ 0 ], p3[ 0 ] ), h( p0[ 1 ], p1[ 1 ], p2[ 1 ], p3[ 1 ] ) ];
  };
  for ( let i = 0; i <= N; i++ ) {
    const t = i / N;
    const [ x, y ] = at( t );
    const [ nx, ny ] = at( Math.min( 1, t + 0.02 ) );
    const dx = nx - x, dy = ny - y;
    const len = Math.hypot( dx, dy ) || 1;
    // taper, with the last tenth rounding off rather than stopping square
    const round = t > 0.9 ? Math.sqrt( Math.max( 0, 1 - Math.pow( ( t - 0.9 ) / 0.1, 2 ) ) ) : 1;
    const half = ( w0 + ( w1 - w0 ) * t ) / 2 * round;
    left.push( [ x - ( -dy / len ) * half, y - ( dx / len ) * half ] );
    right.push( [ x + ( -dy / len ) * half, y + ( dx / len ) * half ] );
  }
  return cw( left.concat( right.reverse() ) );
}

/** The four radial canals + their offshoots. Electric blue. */
export const JELLY_VEINS_MAIN = ( () => {
  const out: string[] = [];
  // Each canal leaves just under the crown and lands a few units inside the hem's
  // own cusp line, so none of them can reach the silhouette.
  const runs: [ number, number ][] = [ [ -0.78, -0.62 ], [ -0.34, -0.26 ], [ 0.34, 0.30 ], [ 0.78, 0.66 ] ];
  for ( let i = 0; i < runs.length; i++ ) {
    const [ endT, midT ] = runs[ i ];
    const x0 = CX + endT * BELL_RX * 0.10;
    const xe = CX + endT * BELL_RX * 0.86;
    const ye = hemAt( xe ) - 6;
    out.push( vein( [
      [ x0, BELL_TOP + 9 ],
      [ CX + midT * BELL_RX * 0.55, BELL_TOP + 22 ],
      [ xe, ye ],
    ], 1.9, 0.8 ) );
    // one offshoot per canal, leaving it about two thirds down and running out
    // toward the rim — this is what makes the network read as branching rather
    // than as four combed lines
    const bx = CX + midT * BELL_RX * 0.72;
    out.push( vein( [
      [ bx, BELL_TOP + 27 ],
      [ CX + endT * BELL_RX * 0.94, BELL_TOP + 32 ],
    ], 1.2, 0.6 ) );
  }
  return out.join( ' ' );
} )();

/** The cross-vessels threaded between the canals, lower in the bell. Pink. */
export const JELLY_VEINS_FINE = ( () => {
  const out: string[] = [];
  const spans: [ number, number, number ][] = [
    [ -0.70, -0.16, 40 ], [ -0.10, 0.44, 44 ], [ 0.24, 0.76, 38 ],
  ];
  for ( const [ a, b, y ] of spans ) {
    out.push( vein( [
      [ CX + a * BELL_RX, y ],
      [ CX + ( a + b ) / 2 * BELL_RX, y + 4.5 ],
      [ CX + b * BELL_RX, y - 1 ],
    ], 1.5, 0.7 ) );
  }
  // two short verticals hanging off the ring, so the pink is a network too and
  // not three arcs floating in the bell
  out.push( vein( [ [ CX - 0.42 * BELL_RX, 44 ], [ CX - 0.36 * BELL_RX, 52 ] ], 1.2, 0.6 ) );
  out.push( vein( [ [ CX + 0.30 * BELL_RX, 47 ], [ CX + 0.36 * BELL_RX, 54 ] ], 1.2, 0.6 ) );
  return out.join( ' ' );
} )();

/** The whole animal as ONE path — bell, arms and tentacles. Every subpath is
 *  clockwise, so a nonzero fill unions them into a single silhouette, which is
 *  what the idle tab and the unentered passcode dot draw. */
export const JELLYFISH = `${ JELLY_BELL } ${ JELLY_ARMS } ${ JELLY_TENTACLES }`;

/**
 * A SIMPLER animal for the wallpaper's smallest grains: the bell with two arms
 * and nothing else.
 *
 * It exists for the same reason Tallinn's simple flake does. On the chat
 * background this is drawn at about a third of the box, where four thin
 * tentacles land under a pixel each and fill in — a jellyfish whose strands have
 * merged into a beard is worse than one drawn with fewer of them.
 */
export const JELLYFISH_SIMPLE = ( () => {
  const arms = [
    strand( CX - 11, HEM_FLOOR - 6, 94, 11, 4.5, 4, 0.2 ),
    strand( CX + 11, HEM_FLOOR - 6, 100, 11, 4.5, 4, 2.4 ),
  ].join( ' ' );
  return `${ JELLY_BELL } ${ arms }`;
} )();

/* ------------------------------------------------------- passcode colours --
 * Held here rather than in the passcode component: line-lock (the preview the
 * marketplace screenshot shows) and line-page (the exported PNGs) both read
 * them, and every time this family kept two copies of a colour they drifted.
 */

/**
 * The four entered jellies, on the BRIGHT TEAL upper zone this skin's passcode
 * screen paints (#2FB8A7, sampled 2026-08-15).
 *
 * They used to be pale bioluminescence aimed at a dark zone, and on the teal the
 * owner actually picked that set measured 1.34 / 1.27 / 1.08 / 1.19 — four ghosts
 * — while the unentered one, being the only dark thing left, came out as the
 * heaviest mark on the screen. The ordering was not weak, it was backwards, and
 * the device screenshot shows it.
 *
 * So the creatures are DEEP now and the water is what glows. Still one hue per
 * digit rather than one hue at four exposures — mint, lagoon, orchid, coral.
 *
 * They were first set five steps deeper than this (4.13 / 3.65 / 4.04 / 3.70 on
 * the zone) and the owner called the whole row too dark, twice. Correcting an
 * inversion had been allowed to overshoot into near-black creatures, which is its
 * own kind of wrong on a bright skin.
 *
 * They measure 1.34 / 1.52 / 1.67 / 1.77 now, matched to the lightness the owner
 * settled the Home glyph and the profile silhouette at — the three read as one
 * theme, which was the point of the last two rounds.
 *
 * The ORDER is the owner's (2026-08-15), and it LEADS with colour circle 5:
 * sand, coral, orchid, lagoon. The row walks warm to cold across the whole
 * palette, and the theme's own water is where it lands rather than where it
 * starts.
 *
 * The FIRST slot is the one to understand before touching it. #E3B87C is the
 * lightest mark in the row — 1.34 on the zone, under even the coral's 1.52, and
 * only 1.21x the unentered dot. It holds its place on HUE rather than on weight:
 * warm sand is the furthest thing from this teal in the whole palette, and the
 * empty dot it has to beat is a cold mint, so the two can never be read for each
 * other. It is the same trade Miami and Roraima settled on.
 *
 * It is also one deliberate step below circle 5 itself (#EAC089, 1.45), taken on
 * the owner's call for a slightly deeper sand. That step is most of the headroom
 * this slot had: #DCAE6E drops to 1.21 and #C9975A to 1.06, which puts an ENTERED
 * digit at the unentered one's weight. There is no second step here.
 *
 * This is the floor. The band is narrow and it is close to where a mark on this
 * teal stops being a creature and becomes a smudge, so the gap over the unentered
 * one was bought back at the other end instead: JELLY_PC_OFF moved TOWARD the
 * zone rather than away from it, which is the only lever left once the lit row
 * cannot go lighter.
 */
export const JELLY_PC_COLORS = [ '#E3B87C', '#C26884', '#8F70B0', '#2782A6' ];

/**
 * ONE tone for every unentered jelly — the frame never changes per digit (the
 * rule Aurora's orbs settled).
 *
 * A PALE MINT, and it sits ABOVE the water rather than inside it. Three values
 * were tried below this one and the last two were the same mistake in two sizes:
 * #5CC9BB (1.23:1 on the zone) and then #4CC0B0 (1.11) were both chosen to keep
 * the filled digits heavier by making the empty one quieter, and at 1.11 the
 * owner could not see it at all on the store listing. An unentered dot has to be
 * legible; it is a slot waiting to be filled, not an absence.
 *
 * So the gap changed hands again, and this time it is not a weight gap at all.
 * At 1.54:1 this is heavier than the sand digit (1.34) and the coral (1.52),
 * which on paper is the inversion this theme keeps shipping. It is not one,
 * because the two states differ by DIRECTION and by SATURATION rather than by
 * weight: every entered creature is a saturated hue DARKER than the zone, and
 * this is a desaturated tint of the zone's own colour, LIGHTER than it. A pale
 * ghost of the background never reads as a filled digit however it measures.
 *
 * It is also the TOP of the range. The skin's own talk-room white (#EDFAF9, 2.32
 * on the zone) was tried one step past it and the owner pulled it straight back:
 * at that point the empty dot is the brightest thing on the lock screen and the
 * row reads as one white creature among four coloured ones rather than as a slot.
 *
 * Reaching for a DARK off-tone is the other failure and this theme shipped it
 * once — the original #2E525A measures 3.45 on this zone, an entered jelly's
 * weight on an unentered digit. The number to watch is not the ratio, it is the
 * saturation: push this toward a colour, either way, and it stops being a ghost.
 */
export const JELLY_PC_OFF = '#85DCD2';
